import React, { useContext, useEffect, useRef, useState } from "react";
import { Animated, StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { Feather } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { DataTable } from "react-native-paper";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { useIsFocused } from "@react-navigation/native";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";
import {
  filterAllSearch,
  resetAllSearch,
  searchAllTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";
import { getMergedTrackerOrders } from "../../../redux/dispatcher/chinaTrackerDispatcher";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

// Define headers for the data table
const tableHeadersData = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
    fontWeight: 700,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.3,
    fontWeight: 700,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.3,
  },
  ItemName: {
    name: "Item Name",
    width: 0.8,
  },
  BPName: {
    name: "Customer",
    width: 0.8,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.3,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.3,
    fontWeight: 700,
  },
  OrderStatus: {
    name: "Status",
    width: 0.4,
    fontWeight: 700,
  },
  incomingStatus: {
    name: "Incoming Status",
    width: 0.4,
    fontWeight: 700,
  },
};

const ChinaTrackerAdvancedSearch: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);

  const {
    allOrders: orders,
    loading,
    loadingIncomingAdd,
  } = useSelector((state: any) => state.chinaTracker);
  const { t } = useTranslation();
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [tableScrollOffset, setTableScrollOffset] = useState(0);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns
  useEffect(() => {
    AsyncStorage.getItem("chinatracker_advanced_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch {}
        }
      },
    );
  }, []);

  const getFilteredHeaders = (baseHeaders: Record<string, any>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(
      ([key]) => !hiddenColumns.includes(key),
    );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, h) => sum + (Number(h.width) || 0),
      0,
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, h]) => sum + (Number(h.width) || 0),
      0,
    );
    const scale =
      totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, any> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "chinatracker_advanced_hidden_columns",
        JSON.stringify(updated),
      );
      return updated;
    });
  };

  const fromPage = (
    page: number,
    itemPerPage: number,
    rows: any[] | undefined,
  ) => {
    return page * itemPerPage + itemPerPage > (rows || []).length
      ? (rows || []).length
      : page * itemPerPage + itemPerPage;
  };

  const [dateFilter, setDateFilter] = useState<string>("all");

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      dispatch(searchAllTrackerOrders(searchedText));
    } else {
      dispatch(resetAllSearch());
    }
  };
  const isFocused = useIsFocused();

  useEffect(() => {
    let isMounted: boolean = true;
    if (isRefresh) {
      dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
      setRefresh(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  useEffect(() => {
    setRefresh(false);
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
    if (dateFilter) {
      dispatch(filterAllSearch(dateFilter));
    }
  }, [isFocused, isRefresh, dateFilter]);

  useEffect(() => {
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
  }, []);

  return (
    <>
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  {
                    width: getInnerContainerWidth() as any,
                    height: "100%" as any,
                  },
                ]}
              >
                <View style={styles.stickyHeaderSection}>
                  <View
                    style={[
                      styles.titleContainer,
                      isSmallDevice && {
                        flexDirection: "column",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.titleText,
                        isSmallDevice && styles.titleTextSmall,
                      ]}
                    >
                      {t("allOrders")}
                    </Text>
                    {!isSmallDevice && (
                      <View style={[styles.searchContainer]}>
                        <LabelBannerWithSearchBar
                          titleName={``}
                          handleSearch={handleSearch}
                          setRefresh={setRefresh}
                          dataToExport={orders}
                          fileName="ChinaTrackerAllOrders"
                          sheetName="China Tracker All Orders Screen"
                          extraControls={
                            <TouchableOpacity
                              ref={settingsBtnRef}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                backgroundColor: "#F3F4F6",
                                justifyContent: "center",
                                alignItems: "center",
                                borderWidth: 1,
                                borderColor: columnSettingsOpen
                                  ? "#818CF8"
                                  : "#E2E8F0",
                              }}
                              onPress={() => {
                                if (settingsBtnRef.current) {
                                  settingsBtnRef.current.measureInWindow(
                                    (
                                      x: number,
                                      y: number,
                                      width: number,
                                      height: number,
                                    ) => {
                                      setSettingsBtnPos({
                                        x: x || 0,
                                        y: y || 0,
                                        width: width || 0,
                                        height: height || 0,
                                      });
                                      setColumnSettingsOpen(
                                        !columnSettingsOpen,
                                      );
                                    },
                                  );
                                } else {
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                }
                              }}
                            >
                              <Feather
                                name="settings"
                                size={15}
                                color={
                                  columnSettingsOpen ? "#56B7FD" : "#6B7280"
                                }
                              />
                            </TouchableOpacity>
                          }
                        />
                      </View>
                    )}
                  </View>
                </View>

                <View
                  style={[{ marginLeft: 15, marginTop: 10, marginBottom: 10 }]}
                >
                  <ScrollView horizontal>
                    <TouchableOpacity onPress={() => setDateFilter("all")}>
                      <Text
                        style={
                          dateFilter === "all"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("all")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("cancelled")}
                    >
                      <Text
                        style={
                          dateFilter === "cancelled"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("cancelled")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("closed")}>
                      <Text
                        style={
                          dateFilter === "closed"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("closed")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("onTime")}>
                      <Text
                        style={
                          dateFilter === "onTime"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("onTime")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("noData")}>
                      <Text
                        style={
                          dateFilter === "noData"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("noData")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("interrupted")}
                    >
                      <Text
                        style={
                          dateFilter === "interrupted"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("interrupted")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("planned")}>
                      <Text
                        style={
                          dateFilter === "planned"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("planned")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("incoming")}>
                      <Text
                        style={
                          dateFilter === "incoming"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("incoming")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("approved")}>
                      <Text
                        style={
                          dateFilter === "approved"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("approved")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("completed")}
                    >
                      <Text
                        style={
                          dateFilter === "completed"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("completed")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("atRisk")}>
                      <Text
                        style={
                          dateFilter === "atRisk"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("atRisk")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => setDateFilter("delayed")}>
                      <Text
                        style={
                          dateFilter === "delayed"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("delayed")}
                      </Text>
                    </TouchableOpacity>
                  </ScrollView>
                </View>

                <ScrollView
                  style={styles.modalContentScroll}
                  contentContainerStyle={styles.modalContentScrollContainer}
                >
                  <View style={styles.scrollBodySection}>
                    <View style={styles.tableScrollWrapper}>
                      <Animated.ScrollView
                        horizontal
                        showsHorizontalScrollIndicator
                        contentContainerStyle={{
                          minWidth: Math.max(screenWidth, 1200),
                        }}
                        scrollEventThrottle={16}
                        onScroll={(event) =>
                          setTableScrollOffset(
                            event.nativeEvent.contentOffset.x || 0,
                          )
                        }
                      >
                        <View
                          style={[
                            styles.containerContent,
                            { minWidth: Math.max(screenWidth, 1200) },
                          ]}
                        >
                          {!(loading || loadingIncomingAdd) ? (
                            <View>
                              <ChinaTrackerDataTableWithPagination2
                                headers={getFilteredHeaders(tableHeaders)}
                                rows={orders}
                                itemsPerPage={10}
                                isDataLoading={isSearched}
                                isDisplayTimeWithDate={true}
                                useModal={true}
                                isDetail={false}
                                isEdit={false}
                                isDelete={false}
                                actionRows={actionRows}
                                setActionRows={setActionRows}
                                itemTracker
                                // Required props
                                setModalVisible={() => {}}
                                handleRowClick={() => {}}
                                setSelected={() => {}}
                                handleDeleteRow={() => {}}
                                operationActions={() => {}}
                                selectable={true}
                                nestedHeader={null}
                                t={t}
                                page={page}
                                setPage={setPage}
                                hidePagination={true}
                              />
                            </View>
                          ) : (
                            <View>
                              <Spinner
                                size="small"
                                color="#74BBFF"
                                styles={{ flexDirection: "row", margin: "20%" }}
                              />
                            </View>
                          )}
                        </View>
                      </Animated.ScrollView>
                    </View>
                  </View>
                </ScrollView>
                <View
                  style={{
                    borderTopWidth: 1,
                    borderTopColor: "#E2E8F0",
                    backgroundColor: "#fff",
                  }}
                >
                  <DataTable.Pagination
                    page={page}
                    numberOfPages={Math.ceil(orders.length / 10)}
                    onPageChange={(p) => setPage(p)}
                    label={`${fromPage(page, 10, orders)} of ${orders.length}`}
                    showFastPaginationControls
                    numberOfItemsPerPage={10}
                    selectPageDropdownLabel={"Rows per page"}
                  />
                </View>
                <View
                  style={[styles.footer, isSmallDevice && styles.footerSmall]}
                >
                  <TouchableOpacity
                    onPress={onClose}
                    style={[
                      styles.closeButton,
                      isSmallDevice && styles.buttonSmall,
                    ]}
                  >
                    <Text style={isSmallDevice && styles.buttonTextSmall}>
                      {t("close")}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                {
                  width: getInnerContainerWidth() as any,
                  height: "95%" as any,
                },
                isSmallDevice && styles.modalContainerMobile,
              ]}
            >
              <View
                style={[
                  styles.titleContainer,
                  isSmallDevice && styles.titleContainerMobile,
                ]}
              >
                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {t("allOrders")}
                </Text>
                <View
                  style={[
                    styles.searchContainer,
                    isSmallDevice && styles.searchContainerMobile,
                  ]}
                >
                  <LabelBannerWithSearchBar
                    titleName={``}
                    handleSearch={handleSearch}
                    setRefresh={setRefresh}
                    dataToExport={orders}
                    fileName="ChinaTrackerRejectedOrders"
                    sheetName="China Tracker Reject Report Screen"
                    extraControls={
                      <TouchableOpacity
                        ref={settingsBtnRef}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          backgroundColor: "#F3F4F6",
                          justifyContent: "center",
                          alignItems: "center",
                          borderWidth: 1,
                          borderColor: columnSettingsOpen
                            ? "#818CF8"
                            : "#E2E8F0",
                        }}
                        onPress={() => {
                          if (settingsBtnRef.current) {
                            settingsBtnRef.current.measureInWindow(
                              (
                                x: number,
                                y: number,
                                width: number,
                                height: number,
                              ) => {
                                setSettingsBtnPos({
                                  x: x || 0,
                                  y: y || 0,
                                  width: width || 0,
                                  height: height || 0,
                                });
                                setColumnSettingsOpen(!columnSettingsOpen);
                              },
                            );
                          } else {
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        }}
                      >
                        <Feather
                          name="settings"
                          size={15}
                          color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                        />
                      </TouchableOpacity>
                    }
                  />
                </View>
              </View>

              <View
                style={[{ marginLeft: 15, marginTop: 10, marginBottom: 10 }]}
              >
                <ScrollView horizontal>
                  <TouchableOpacity onPress={() => setDateFilter("all")}>
                    <Text
                      style={
                        dateFilter === "all"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("all")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("cancelled")}>
                    <Text
                      style={
                        dateFilter === "cancelled"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("cancelled")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("closed")}>
                    <Text
                      style={
                        dateFilter === "closed"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("closed")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("onTime")}>
                    <Text
                      style={
                        dateFilter === "onTime"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("onTime")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("noData")}>
                    <Text
                      style={
                        dateFilter === "noData"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("noData")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => setDateFilter("interrupted")}
                  >
                    <Text
                      style={
                        dateFilter === "interrupted"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("interrupted")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("planned")}>
                    <Text
                      style={
                        dateFilter === "planned"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("planned")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("incoming")}>
                    <Text
                      style={
                        dateFilter === "incoming"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("incoming")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("approved")}>
                    <Text
                      style={
                        dateFilter === "approved"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("approved")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("completed")}>
                    <Text
                      style={
                        dateFilter === "completed"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("completed")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("atRisk")}>
                    <Text
                      style={
                        dateFilter === "atRisk"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("atRisk")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => setDateFilter("delayed")}>
                    <Text
                      style={
                        dateFilter === "delayed"
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("delayed")}
                    </Text>
                  </TouchableOpacity>
                </ScrollView>
              </View>

              <ScrollView
                style={styles.modalContentScroll}
                contentContainerStyle={styles.modalContentScrollContainer}
              >
                <View
                  style={[
                    styles.scrollBodySection,
                    isSmallDevice && styles.scrollBodySectionMobile,
                  ]}
                >
                  <View style={styles.tableScrollWrapper}>
                    <Animated.ScrollView
                      horizontal
                      showsHorizontalScrollIndicator
                      contentContainerStyle={{
                        minWidth: Math.max(screenWidth, 1200),
                      }}
                      scrollEventThrottle={16}
                      onScroll={(event) =>
                        setTableScrollOffset(
                          event.nativeEvent.contentOffset.x || 0,
                        )
                      }
                    >
                      <View
                        style={[
                          styles.containerContent,
                          { minWidth: Math.max(screenWidth, 1200) },
                        ]}
                      >
                        {!(loading || loadingIncomingAdd) ? (
                          <View>
                            <ChinaTrackerDataTableWithPagination2
                              headers={getFilteredHeaders(tableHeaders)}
                              rows={orders}
                              itemsPerPage={10}
                              isDataLoading={isSearched}
                              isDisplayTimeWithDate={true}
                              useModal={true}
                              isDetail={false}
                              isEdit={false}
                              isDelete={false}
                              actionRows={actionRows}
                              setActionRows={setActionRows}
                              itemTracker
                              // Required props
                              setModalVisible={() => {}}
                              handleRowClick={() => {}}
                              setSelected={() => {}}
                              handleDeleteRow={() => {}}
                              page={page}
                              setPage={setPage}
                              selectable={true}
                              hidePagination={true}
                            />
                          </View>
                        ) : (
                          <View>
                            <Spinner
                              size="small"
                              color="#74BBFF"
                              styles={{ flexDirection: "row", margin: "20%" }}
                            />
                          </View>
                        )}
                      </View>
                    </Animated.ScrollView>
                  </View>
                </View>
              </ScrollView>
              <View
                style={{
                  borderTopWidth: 1,
                  borderTopColor: "#E2E8F0",
                  backgroundColor: "#fff",
                }}
              >
                <DataTable.Pagination
                  page={page}
                  numberOfPages={Math.ceil(orders.length / 10)}
                  onPageChange={(p) => setPage(p)}
                  label={`${fromPage(page, 10, orders)} of ${orders.length}`}
                  showFastPaginationControls
                  numberOfItemsPerPage={10}
                  selectPageDropdownLabel={"Rows per page"}
                />
              </View>
              <View
                style={[styles.footer, isSmallDevice && styles.footerSmall]}
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={[
                    styles.closeButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text style={isSmallDevice && styles.buttonTextSmall}>
                    {t("close")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>

      {columnSettingsOpen && (
        <Modal
          transparent={true}
          visible={columnSettingsOpen}
          animationType="fade"
          onRequestClose={() => setColumnSettingsOpen(false)}
        >
          <TouchableOpacity
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.1)",
            }}
            activeOpacity={1}
            onPress={() => setColumnSettingsOpen(false)}
          >
            <TouchableOpacity
              activeOpacity={1}
              style={{
                position: "absolute",
                top: isSmallDevice
                  ? "50%"
                  : settingsBtnPos.y + settingsBtnPos.height + 6,
                left: isSmallDevice ? "50%" : settingsBtnPos.x - 180 + 40,
                transform: isSmallDevice
                  ? [{ translateX: -110 }, { translateY: -150 }]
                  : [],
                backgroundColor: "#fff",
                borderRadius: 12,
                borderWidth: 1,
                borderColor: "#E2E8F0",
                paddingVertical: 10,
                minWidth: 220,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.15,
                shadowRadius: 20,
                elevation: 10,
                zIndex: 9999,
              }}
              onPress={(e) => e.stopPropagation()}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: "#6B7280",
                  paddingHorizontal: 16,
                  paddingBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Show / Hide Columns
              </Text>
              <View
                style={{
                  height: 1,
                  backgroundColor: "#F1F5F9",
                  marginBottom: 4,
                }}
              />
              {/* Select All */}
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                }}
                onPress={() => {
                  const allKeys = Object.keys(tableHeaders).filter(
                    (k) => k !== "",
                  );
                  if (hiddenColumns.length === 0) {
                    const updated = [...allKeys];
                    setHiddenColumns(updated);
                    AsyncStorage.setItem(
                      "chinatracker_advanced_hidden_columns",
                      JSON.stringify(updated),
                    );
                  } else {
                    setHiddenColumns([]);
                    AsyncStorage.setItem(
                      "chinatracker_advanced_hidden_columns",
                      JSON.stringify([]),
                    );
                  }
                }}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 1.5,
                    borderColor:
                      hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                    backgroundColor:
                      hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 10,
                  }}
                >
                  {hiddenColumns.length === 0 && (
                    <Feather name="check" size={13} color="#fff" />
                  )}
                </View>
                <Text
                  style={{
                    fontSize: 14,
                    color: "#111827",
                    fontWeight: "700",
                  }}
                >
                  Select All
                </Text>
              </TouchableOpacity>
              <View
                style={{
                  height: 1,
                  backgroundColor: "#F1F5F9",
                  marginBottom: 2,
                }}
              />
              <ScrollView
                style={{ maxHeight: 300 }}
                showsVerticalScrollIndicator={true}
              >
                {Object.entries(tableHeaders).map(([key, value]: any) => {
                  if (
                    key === "" ||
                    (value as any).display === "none" ||
                    (value as any).display === "hidden"
                  )
                    return null;
                  const isVisible = !hiddenColumns.includes(key);
                  return (
                    <TouchableOpacity
                      key={key}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 8,
                        paddingHorizontal: 16,
                      }}
                      onPress={() => toggleColumn(key)}
                    >
                      <View
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          borderWidth: 1.5,
                          borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                          backgroundColor: isVisible ? "#56B7FD" : "#fff",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: 10,
                        }}
                      >
                        {isVisible && (
                          <Feather name="check" size={13} color="#fff" />
                        )}
                      </View>
                      <Text
                        style={{
                          fontSize: 14,
                          color: "#374151",
                          flex: 1,
                        }}
                      >
                        {String(value.name || key)}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: 8,
    width: 200,
  },
  searchButton: {
    backgroundColor: "#f0f0f0",
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: "#D8D8D8",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  tableScrollWrapper: {
    flex: 1,
    width: "100%",
  },
  modalContentScroll: {
    flex: 1,
  },
  modalContentScrollContainer: {
    paddingBottom: 20,
  },
  stickyHeaderSection: {
    backgroundColor: "#fff",
    paddingBottom: 12,
    zIndex: 10,
  },
  scrollBodySection: {
    paddingTop: 12,
  },
  scrollBodySectionMobile: {
    paddingTop: 4,
  },
  modalContainerMobile: {
    padding: 10,
    borderRadius: 10,
  },
  titleContainerMobile: {
    flexDirection: "column",
    alignItems: "flex-start",
    paddingVertical: 8,
    marginBottom: 4,
    gap: 6,
  },
  searchContainerMobile: {
    width: "100%",
  },

  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#F1F5F9",
    color: "#4B5563",
    overflow: "hidden",
  },

  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#56B7FD",
    color: "#fff",
    overflow: "hidden",
  },
});

export default ChinaTrackerAdvancedSearch;
